import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { table } from '../../config/styles';
import { dataGridProps } from '../../helpers/dataGrid';
import useBreakpoints from '../../helpers/useBreakpoints';
import { isFetchingMessages } from '../../modules/localization/selectors';
import DataGrid from '../DataGrid';
import DataGridRowSkeleton from '../DataGridRowSkeleton';
import EllipsisText from '../EllipsisText';
import AccessLogAccessType from './components/AccessLogAccessType';
import AccessLogStartTime from './components/AccessLogStartTime';
import AccessLogUser from './components/AccessLogUser';
import FilterHead from './components/FilterHead';
import messages from './messages';
import NoRowsOverlay from './NoRowsOverlay';
import NoRowsSearchOverlay from './NoRowsSearchOverlay';

const styles = {
  noResults: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  root: {
    px: 0,
    py: 2,
  },
  table: table(),
};

const AccessLogDataGrid = ({
  accessLogsTable,
  data,
  isFetching,
  onAccessLogShowDetail,
  onChangeFilter,
  onOrderBy,
  onRowsPerPageChange,
  onSelectPage,
}) => {
  const { formatMessage } = useIntl();
  const { upLg, upMd, upSm } = useBreakpoints();
  const isLocalizationLoaded = useSelector(isFetchingMessages);

  const newColumns = useMemo(
    () => [
      {
        disableColumnMenu: true,
        field: 'startAt',
        headerName: upSm ? formatMessage(messages.accessTypeTooltipColumnsTime) : '',
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && <AccessLogStartTime accessLog={cell.value} displayTime={upSm} />}
          </DataGridRowSkeleton>
        ),
        sortable: true,
        width: upSm ? 200 : 50,
      },
      {
        disableColumnMenu: true,
        field: 'user',
        flex: 1,
        headerName: formatMessage(messages.accessTypeTooltipColumnsUser),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && <AccessLogUser accessLog={cell.value} />}
          </DataGridRowSkeleton>
        ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'device',
        flex: 1,
        headerName: formatMessage(messages.accessTypeTooltipColumnsDevice),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && <EllipsisText>{cell.value?.device.name}</EllipsisText>}
          </DataGridRowSkeleton>
        ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'accessMethod',
        headerName: formatMessage(
          upLg
            ? messages.accessTypeTooltipColumnsAccessMethodDesktop
            : messages.accessTypeTooltipColumnsAccessMethodMobile
        ),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && <AccessLogAccessType accessLog={cell.value} />}
          </DataGridRowSkeleton>
        ),
        sortable: false,
        width: upLg ? 120 : 80,
      },
      {
        disableColumnMenu: true,
        field: 'detail',
        headerName: formatMessage(messages.accessTypeTooltipColumnsDetail),
        renderCell: (cell) => (
          <DataGridRowSkeleton isFetching={isFetching}>
            {cell?.value && <EllipsisText>{cell.value?.detail?.log || '—'}</EllipsisText>}
          </DataGridRowSkeleton>
        ),
        sortable: false,
        width: upLg ? 256 : 200,
      },
    ],
    [upSm, upMd, upLg, isFetching, isLocalizationLoaded]
  );

  const rows = useMemo(
    () =>
      data.map((accessLog) => ({
        accessMethod: accessLog,
        detail: accessLog,
        device: accessLog,
        id: accessLog.id,
        isClickable: true,
        startAt: accessLog,
        user: accessLog,
      })),
    [data]
  );

  return (
    <Paper sx={styles.root}>
      <FilterHead accessLogsTable={accessLogsTable} onChangeFilter={onChangeFilter} />
      <DataGrid
        columns={newColumns}
        columnVisibilityModel={{
          accessMethod: upSm,
          detail: upLg,
        }}
        disableRowSelectionOnClick
        hideFooterPagination={false}
        loading={isFetching}
        noRowsOverlay={
          accessLogsTable.filter?.fulltext !== '' ? (
            <NoRowsSearchOverlay
              onClearSearch={() =>
                onChangeFilter(
                  {
                    target: {
                      name: 'fulltext',
                      value: '',
                    },
                  },
                  true
                )
              }
              searchText={accessLogsTable.filter.fulltext}
            />
          ) : (
            <NoRowsOverlay />
          )
        }
        onCellClick={(params) => onAccessLogShowDetail(params.value.id)}
        onOrderBy={onOrderBy}
        onRowsPerPageChange={onRowsPerPageChange}
        onSelectPage={onSelectPage}
        order={accessLogsTable.order}
        page={accessLogsTable.page}
        resultsFiltered={accessLogsTable.filter?.results ? accessLogsTable.resultsAlert : accessLogsTable.resultsTotal}
        resultsTotal={accessLogsTable.resultsTotal}
        rows={rows}
        rowsPerPage={accessLogsTable.rowsPerPage}
      />
    </Paper>
  );
};

AccessLogDataGrid.propTypes = {
  accessLogsTable: dataGridProps.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFetching: PropTypes.bool.isRequired,
  onAccessLogShowDetail: PropTypes.func.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  onOrderBy: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onSelectPage: PropTypes.func.isRequired,
};

export default AccessLogDataGrid;
